import type {
    DailyDaySummaryDto,
    DailyExecutiveKpiDto,
    DailyMoneySectionDto,
    MetricDto,
} from "@/features/daily/types/daily.types";
import { DailyTone, commandViewModel, dailyFormat } from "@/features/daily/components/command/command-view-model";
import { executiveViewModel } from "@/features/daily/components/executive/executive-view-model";
import { QUIET_HOUR_COPY } from "@/features/daily/components/command/health-view-model";
import { DeltaVm, StatTileVm, statTileViewModel } from "@/features/daily/components/stat-tile-view-model";
import { moneyByType } from "@/features/daily/components/money-by-type";

export type DaySummaryCardVm = {
    key: string;
    label: string;
    /** One line per currency for money; a single line otherwise. Never a sum across currencies. */
    values: string[];
    valueTone: DailyTone;
    note: string;
    reasons: string[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

/** D24: the cache stores `unknown`; the page proves the shape instead of casting. */
export const isDaySummaryDto = (value: unknown): value is DailyDaySummaryDto =>
    isRecord(value)
    && isRecord(value.envelope)
    && typeof value.date === "string"
    && isRecord(value.volume)
    && isRecord(value.revenue)
    && isRecord(value.successRate)
    && isRecord(value.slowOrders)
    && isRecord(value.endToEnd)
    && isRecord(value.fundingWait)
    && isRecord(value.feeDeviation)
    && isRecord(value.creationGap);

const SHORT_LABEL: Record<string, string> = {
    volume: "Volumen",
    revenue: "Ingresos",
    endToEnd: "P95",
    funding: "P95 fondos",
    fee: "Desvío de fee",
    anomaly: "Anomalías",
};

const signed = new Intl.NumberFormat("es-ES", { minimumFractionDigits: 1, maximumFractionDigits: 1, signDisplay: "exceptZero" });

const qualified = (metric: MetricDto, text: string): string =>
    metric.quality === "ok" ? text : `${text} · ${commandViewModel.qualityLabel(metric.quality)}`;

const reasonsOf = (...metrics: MetricDto[]): string[] =>
    Array.from(new Set(metrics.flatMap((metric) => commandViewModel.reasonTexts(metric.reasonCodes))));

/** FR-014 + D103: a money card lists each currency on its own line; unmeasured is a dash with its reason. */
const moneyCard = (key: string, label: string, section: DailyMoneySectionDto, note: string): DaySummaryCardVm => {
    const measured = section.quality !== "error" && section.quality !== "unsupported";
    const values = !measured || section.byCurrency.length === 0
        ? [dailyFormat.absent]
        : commandViewModel.impactRows(section.byCurrency).map((row) => row.amountLabel);
    return {
        key,
        label,
        values,
        valueTone: section.quality === "error" ? "critical" : "neutral",
        note: !measured
            ? commandViewModel.qualityLabel(section.quality)
            : section.byCurrency.length === 0 ? "Ninguna operación completada ese día" : note,
        reasons: commandViewModel.reasonTexts(section.reasonCodes),
    };
};

const comparison = (kpi: DailyExecutiveKpiDto): string => {
    if (kpi.reference === null) return "Sin día de referencia";
    const name = executiveViewModel.referenceLabel(kpi.reference.label);
    const base = dailyFormat.ratioPercent(kpi.reference.value);
    if (kpi.deltaPp !== null) return `${signed.format(kpi.deltaPp)} pp vs ${name} (${base})`;
    if (kpi.deltaPct !== null) return `${signed.format(kpi.deltaPct)}% vs ${name} (${base})`;
    return `Sin cambio comparable frente a ${name} (${base})`;
};

const anomalyCard = (gap: DailyDaySummaryDto["creationGap"]): DaySummaryCardVm => {
    const threshold = dailyFormat.duration(gap.thresholdMs);
    const measuredText = gap.gap.value === null
        ? "Silencio sin medir"
        : gap.mode === "since_last"
            ? `Sin operaciones nuevas desde hace ${dailyFormat.duration(gap.gap.value)}`
            : `Mayor silencio del día: ${dailyFormat.duration(gap.gap.value)}`;
    // D108/B6: a silence the baseline expects is information, never an alarm.
    if (gap.expectedByBaseline === true) {
        return {
            key: "anomaly",
            label: "Vigilancia de anomalías",
            values: ["Tranquilo (esperado)"],
            valueTone: "info",
            note: `${QUIET_HOUR_COPY} · ${measuredText}`,
            reasons: reasonsOf(gap.gap),
        };
    }
    // The words carry the state; `null` means the silence could not be judged at all.
    const status = gap.attention === null ? dailyFormat.absent : gap.attention ? "Atención" : "Normal";
    return {
        key: "anomaly",
        label: "Vigilancia de anomalías",
        values: [status],
        valueTone: gap.attention === true ? "warning" : "neutral",
        note: `${measuredText} · umbral ${threshold}`,
        reasons: reasonsOf(gap.gap),
    };
};

export const daySummaryViewModel = {
    cards: (dto: DailyDaySummaryDto): DaySummaryCardVm[] => {
        const slow = dto.slowOrders;
        const success = dto.successRate.metric;
        return [
            moneyCard("volume", "Volumen del día", dto.volume, "Dinero recibido ese día, por moneda"),
            moneyCard("revenue", "Ingresos del día", dto.revenue, "Por moneda de la comisión"),
            {
                key: "success",
                label: "Tasa de éxito",
                values: [dailyFormat.ratioPercent(success.value)],
                valueTone: success.quality === "error" ? "critical" : "neutral",
                note: qualified(success, comparison(dto.successRate)),
                reasons: [
                    // D133: the tile is commercial conversion; the funded-operational rate is secondary.
                    "Conversión: operaciones con fondos recibidos sobre creadas · sin canceladas",
                    ...(dto.fundedSuccessRate === undefined ? [] : [`Sobre financiadas: ${dailyFormat.kpiPercent(dto.fundedSuccessRate.value, 2)}`]),
                    ...reasonsOf(success),
                ],
            },
            {
                key: "slow",
                label: "Órdenes lentas",
                values: [dailyFormat.count(slow.count.value)],
                valueTone: slow.count.value !== null && slow.count.value > 0 ? "warning" : "neutral",
                note: qualified(
                    slow.count,
                    `${dailyFormat.ratioPercent(slow.rate.value)} de las completadas · objetivo ${dailyFormat.duration(slow.targetMs)} · salud SLA ${dailyFormat.ratioPercent(slow.slaHealth.value)}`,
                ),
                reasons: reasonsOf(slow.count, slow.rate, slow.slaHealth),
            },
            {
                key: "endToEnd",
                label: "P95 extremo a extremo",
                values: [dailyFormat.duration(dto.endToEnd.p95.value)],
                valueTone: "neutral",
                note: qualified(
                    dto.endToEnd.p95,
                    `P50 ${dailyFormat.duration(dto.endToEnd.p50.value)} · ${dailyFormat.counted(dto.endToEnd.p95.sample, "operación", "operaciones")}`,
                ),
                reasons: reasonsOf(dto.endToEnd.p50, dto.endToEnd.p95),
            },
            {
                key: "funding",
                label: "P95 de financiación",
                values: [dailyFormat.duration(dto.fundingWait.p95.value)],
                valueTone: "neutral",
                // Enmienda C5: the payer's time is information, never a breach.
                note: qualified(
                    dto.fundingWait.p95,
                    `P50 ${dailyFormat.duration(dto.fundingWait.p50.value)} · ${dailyFormat.counted(dto.fundingWait.p95.sample, "financiada", "financiadas")} · tiempo del cliente, no un plazo`,
                ),
                reasons: reasonsOf(dto.fundingWait.p50, dto.fundingWait.p95),
            },
            {
                key: "fee",
                label: "Desviación de fee",
                values: [dailyFormat.count(dto.feeDeviation.count.value)],
                valueTone: dto.feeDeviation.count.value !== null && dto.feeDeviation.count.value > 0 ? "warning" : "neutral",
                note: qualified(dto.feeDeviation.count, `${dailyFormat.ratioPercent(dto.feeDeviation.rate.value)} de las operaciones`),
                reasons: reasonsOf(dto.feeDeviation.count, dto.feeDeviation.rate),
            },
            anomalyCard(dto.creationGap),
        ];
    },

    /**
     * The same eight figures as identical stat tiles (three lines each). Money shows its
     * first currency compacted and how many more there are; every exact amount, the
     * full explanation and the reasons go into the tooltip.
     */
    tiles: (dto: DailyDaySummaryDto): StatTileVm[] => {
        const moneyOf = (section: DailyMoneySectionDto) => commandViewModel.impactRows(section.byCurrency);
        const success = dto.successRate;
        const successDelta: DeltaVm | null = success.deltaPp === null
            ? null
            : { label: dailyFormat.kpiDelta(success.deltaPp, " pp"), direction: success.deltaPp > 0 ? "up" : success.deltaPp < 0 ? "down" : "flat", good: success.deltaPp === 0 ? null : success.deltaPp > 0 };
        // D130: KPI figures in the legacy board's format; the cards' running text stays as it is.
        const kpiValue: Record<string, string> = {
            success: dailyFormat.kpiPercent(success.metric.value, 2),
            slow: dailyFormat.exactCount(dto.slowOrders.count.value),
            endToEnd: dailyFormat.kpiDuration(dto.endToEnd.p95.value),
            funding: dailyFormat.kpiDuration(dto.fundingWait.p95.value),
            fee: dailyFormat.exactCount(dto.feeDeviation.count.value),
        };
        // D136: what each currency's volume and revenue are made of.
        const typeLines = (key: string): string[] => {
            if (key === "volume") return dto.volume.byCurrency.flatMap((row) => moneyByType.volumeLines(row));
            if (key === "revenue") return dto.revenue.byCurrency.flatMap((row) => moneyByType.revenueLines(row));
            return [];
        };
        return daySummaryViewModel.cards(dto).map((card) => {
            const value = kpiValue[card.key] ?? card.values[0];
            const rows = card.key === "volume" ? moneyOf(dto.volume) : card.key === "revenue" ? moneyOf(dto.revenue) : null;
            const measuredMoney = rows !== null && card.values[0] !== dailyFormat.absent && rows.length > 0;
            const first = rows === null ? undefined : rows[0];
            const shortNote = card.note.split(" · ")[0];
            const exactLines = rows === null ? [] : rows.map((row) => dailyFormat.exactMoney(row.amount, row.currency));
            return statTileViewModel.tile({
                key: card.key,
                label: SHORT_LABEL[card.key] ?? card.label,
                fullLabel: card.label,
                value: measuredMoney && first !== undefined ? dailyFormat.compactMoney(first.amount, first.currency) : value,
                valueExact: measuredMoney ? exactLines.join(" · ") : `${card.label}: ${value}`,
                valueTone: card.valueTone,
                meta: card.key === "success" && successDelta !== null
                    ? null
                    : measuredMoney && rows !== null && rows.length > 1 ? `${shortNote} · +${rows.length - 1} monedas` : shortNote,
                delta: card.key === "success" ? successDelta : null,
                tooltip: [card.note, ...exactLines, ...typeLines(card.key), ...card.reasons],
            });
        });
    },
};
